import ContentConfig from '../config/ContentConfig';
import LocalState, { ReporterState } from '../state/LocalState';
import PendingReport from '../common/Reporter/report/PendingReport';
import getNodeInfo, { NodeInfoData, OptionalNodeInfoData } from './getNodeInfo';
import logger from '../log';
import { Ecdn11Agent, Ecdn11Block } from '../reporter/report/Report';
import checkForInterceptor from '../util/checkForInterceptor';
import InterceptorName from '../interceptor/InterceptorName';
import isValidGuid from '../util/isValidGuid';

const log = logger.getLogger(__filename);

const CHECK_TIMEOUT = 2000;
export const MIN_REPORT_TIME = 5000;
const { LOCAL_AGENT } = InterceptorName;

const maybeUpdate11AgentData = async (
  state: LocalState,
  report: PendingReport
): Promise<boolean> => {
  const config = ContentConfig.getInstance();
  const { contentId, tenantId } = state.play;
  const hasInterceptor = checkForInterceptor(LOCAL_AGENT, state);
  if (!hasInterceptor || !tenantId || !contentId) {
    log.debug('Unable to look for state', {
      hasInterceptor,
      hasTenantId: !!tenantId,
      hasContentId: !!contentId
    });
    return false;
  }
  if (!report.ecdn11) {
    report.ecdn11 = new Ecdn11Block();
  }
  if (!report.ecdn11.agent) {
    report.ecdn11.agent = new Ecdn11Agent();
  }
  const { agent } = report.ecdn11;
  const lastCheck = state.reporter.ecdn11LastCheck || 0;
  const now = Date.now();
  const { reporter } = state;
  if (!needsRefresh({ lastCheck, agent, reporter, now, config })) {
    log.trace('No refresh for agent data needed', {
      lastCheck,
      agent,
      reporter,
      now,
      config
    });
    return false;
  }
  reporter.ecdn11LastCheck = now;
  const controller = new AbortController();
  setTimeout(() => {
    controller.abort();
  }, CHECK_TIMEOUT);
  log.debug('Calling getNodeInfo');
  return getNodeInfo({ config, contentId, controller, tenantId })
    .then((data: OptionalNodeInfoData) =>
      updateAgentClusterId({ data, state, agent })
    )
    .catch(e => {
      log.warn('Failed to get node info', e);
      return false;
    });
};

export interface UpdateClusterIdArgs {
  data: OptionalNodeInfoData;
  state: LocalState;
  agent: Ecdn11Agent;
}

export const updateAgentClusterId = (args: UpdateClusterIdArgs): boolean => {
  const { data: optionalData, state, agent } = args;
  const { reporter } = state;
  if (!optionalData) {
    return false;
  }
  const data: NodeInfoData = optionalData as NodeInfoData;
  log.info('Updating ecdn11 agent state:', { data, agent });
  if (isNewValidGuid(data.activeClusterId, reporter.activeClusterId)) {
    state.reporter.initialReportDataSent = false;
    state.reporter.agentClusterId = data.activeClusterId;
    agent.activeClusterId = data.activeClusterId;
  }
  agent.agentId = data.agentId;
  agent.nodeId = data.nodeId;
  return true;
};

export const isNewValidGuid = (newGuid?: string, oldGuid?: string): boolean => {
  return isValidGuid(newGuid) && newGuid != oldGuid;
};

export interface NeedsRefreshArgs {
  lastCheck: number;
  agent: Ecdn11Agent;
  reporter: ReporterState;
  now: number;
  config: ContentConfig;
}

export const needsRefresh = (data: NeedsRefreshArgs): boolean => {
  // Only check at most once every MIN_REPORT_TIME ms. Additionally only
  // check if we are missing data or we haven't checked in at least the report
  // interval.
  return (
    data.lastCheck + data.config.report.i < data.now ||
    (data.lastCheck + MIN_REPORT_TIME < data.now &&
      (!data.agent.agentId ||
        !data.agent.nodeId ||
        !data.agent.activeClusterId))
  );
};

export default maybeUpdate11AgentData;
